import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

// Shape of GET /api/dashboard-kpi/owner (src/api-dashboard-kpi/owner.js).
// A section the server could not compute arrives as null — show
// "unavailable", never a zero.

export interface PeriodFigure { value: number; count: number; previous: number; change: number | null }

export interface OwnerDashboardData {
  period: { from: string; to: string; days: number; previousFrom: string; previousTo: string };
  generatedAt: string;
  money: {
    sales: PeriodFigure;
    collected: PeriodFigure;
    quotations: PeriodFigure;
    receivables: {
      total: number; overdue: number; invoices: number;
      aging: { key: string; label: string; value: number; count: number }[];
      topDebtors: { customerId: string; name: string; due: number; overdue: number; invoices: number; oldestDays: number }[];
    };
  } | null;
  trend: { granularity: 'day' | 'month'; points: { key: string; invoiced: number; collected: number }[] } | null;
  leads: {
    created: number; createdPrevious: number; change: number | null;
    converted: number; lost: number; conversionRate: number | null;
    byStage: { stage: string; count: number; value: number }[];
    bySource: { source: string; count: number }[];
    lostReasons: { reason: string; count: number }[];
    openPipeline: { count: number; value: number };
    enquiries: { received: number; waitingToBeAssigned: number };
  } | null;
  team: {
    userId: string; name: string; role: string; designation?: string; phone?: string;
    leadsNew: number; leadsOpen: number; pipelineValue: number;
    quotations: number; quotationValue: number;
    invoices: number; sales: number; collected: number;
    overdueFollowups: number; monthTarget: number; lastSeenAt: string | null;
  }[] | null;
  customers: {
    litresSold: number;
    topCustomers: { customerId: string; name: string; value: number; invoices: number }[];
    topProducts: { name: string; quantity: number; litres: number; value: number }[];
  } | null;
  dispatch: {
    awaiting: { count: number; value: number };
    overdue: { count: number; value: number };
    dispatched: { count: number; value: number };
    samplesToDispatch: number;
  } | null;
  attention: {
    editApprovals: number; materialApprovals?: number; leaves: number; overdueFollowups: number; followupsDueToday: number;
    enquiries: number; draftInvoices: number; quotationsExpiring: number; samplesAwaitingFeedback: number;
  } | null;
}

export function useOwnerDashboard(from: string, to: string) {
  return useQuery({
    queryKey: ['dashboard', 'owner', from, to],
    queryFn: async () => (await api.get<OwnerDashboardData>('/api/dashboard-kpi/owner', { params: { from, to } })).data,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    refetchInterval: 120_000,
  });
}
